/**
 * MerchantAgentCooldownSettings — control the reapply cooldown for rejected
 * agent applications on the merchant platform.
 *
 * Route: /control/cloudgm-business/agent-cooldown
 *
 * When cooldown is ON:
 *   Rejected agent must wait N days before they can reapply.
 *   The merchant platform reads this setting from GET /api/agent-invites/reapply-cooldown
 *   with the shared X-AI-Core-Key.
 *
 * When cooldown is OFF:
 *   Rejected agent can reapply immediately (and repeatedly).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Timer, Loader2, Save, Info, Power, Shield } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MerchantAgentCooldownSettings() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState(7);
  const [meta, setMeta] = useState(null); // { updated_at, updated_by }

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/merchant-platform-settings`,
          { headers: getAuthHeaders() },
        );
        setEnabled(Boolean(res.data?.agent_reapply_cooldown_enabled));
        setDays(Number(res.data?.agent_reapply_cooldown_days) || 7);
        setMeta({
          updated_at: res.data?.updated_at,
          updated_by: res.data?.updated_by,
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || t('فشل تحميل الإعدادات', 'Failed to load settings'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    if (days < 1 || days > 90) {
      toast.error(t('عدد الأيام يجب أن يكون بين 1 و 90', 'Days must be between 1 and 90'));
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API_URL}/api/admin/merchant-platform-settings`,
        {
          agent_reapply_cooldown_enabled: enabled,
          agent_reapply_cooldown_days: Number(days),
        },
        { headers: getAuthHeaders() },
      );
      setMeta({
        updated_at: res.data?.updated_at,
        updated_by: res.data?.updated_by,
      });
      toast.success(t('✓ تم الحفظ — منصة التجار ستعتمد الإعدادات الجديدة فوراً', '✓ Saved — merchant platform picks up the new settings immediately'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'} data-testid="merchant-cooldown-page">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <DashboardHeader onBack={() => navigate('/control/cloudgm-business')} />

      {/* Breadcrumb */}
      <div className="border-b border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 text-sm flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate('/dashboard')} className="text-[#71717A] hover:text-[#d4af37] transition">
              {t('لوحة التحكم', 'Dashboard')}
            </button>
            <span className="text-[#71717A]">&lt;</span>
            <button onClick={() => navigate('/control')} className="text-[#71717A] hover:text-[#d4af37] transition">
              {t('التحكم والمعلومات', 'Control & Info')}
            </button>
            <span className="text-[#71717A]">&lt;</span>
            <button onClick={() => navigate('/control/cloudgm-business')} className="text-[#71717A] hover:text-[#d4af37] transition">
              CLOUDGM FZ LLC
            </button>
            <span className="text-[#71717A]">&lt;</span>
            <span className="text-[#d4af37]">
              {t('التحكم بمدة تقديم طلب الوكيل', 'Agent Reapply Cooldown')}
            </span>
          </div>
        </div>
      </div>

      <main className="relative z-10 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/20">
              <Timer className="w-7 h-7 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                {t('التحكم بمدة تقديم طلب الوكيل', 'Agent Reapply Cooldown')}
              </h1>
              <p className="text-white/50 text-sm mt-1">
                {t(
                  'حدّد ما إذا كان الوكيل المرفوض يمكنه إعادة التقديم فوراً أم يجب أن ينتظر عدداً من الأيام.',
                  'Decide whether a rejected applicant can reapply immediately or must wait a set number of days.',
                )}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Enable / Disable */}
              <section className="bg-white/[0.02] rounded-2xl border border-white/10 p-6 mb-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Power className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-semibold">
                        {t('تفعيل مدة الانتظار', 'Enable cooldown')}
                      </h3>
                      <p className="text-white/60 text-sm mt-0.5">
                        {t(
                          'عند التفعيل — أي وكيل تُرفض طلبه لن يستطيع التقديم مجدداً إلا بعد انتهاء المدة المحدّدة.',
                          'When ON — any rejected applicant cannot reapply until the cooldown expires.',
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setEnabled((v) => !v)}
                    className={`relative w-14 h-8 rounded-full transition ${
                      enabled ? 'bg-emerald-500' : 'bg-white/10'
                    }`}
                    data-testid="cooldown-toggle"
                  >
                    <span
                      className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${
                        enabled ? (isAr ? 'right-1' : 'left-7') : (isAr ? 'right-7' : 'left-1')
                      }`}
                    />
                  </button>
                </div>
              </section>

              {/* Days input */}
              <section
                className={`bg-white/[0.02] rounded-2xl border border-white/10 p-6 mb-4 transition ${
                  enabled ? '' : 'opacity-40 pointer-events-none'
                }`}
              >
                <label className="block text-white font-semibold mb-1">
                  {t('عدد الأيام قبل السماح بإعادة التقديم', 'Days before reapply is allowed')}
                </label>
                <p className="text-white/50 text-xs mb-4">
                  {t('نطاق مسموح: 1 – 90 يوم', 'Allowed range: 1 – 90 days')}
                </p>
                <div className="flex items-center gap-2 max-w-xs">
                  <button
                    type="button"
                    onClick={() => setDays((d) => Math.max(1, Number(d) - 1))}
                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={days}
                    onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))}
                    className="flex-1 h-10 bg-[#050505] border border-white/10 rounded-lg text-center text-white text-lg font-bold"
                    data-testid="cooldown-days-input"
                  />
                  <button
                    type="button"
                    onClick={() => setDays((d) => Math.min(90, Number(d) + 1))}
                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition"
                  >
                    +
                  </button>
                  <span className="text-white/50 text-sm">
                    {t('يوم', 'day(s)')}
                  </span>
                </div>

                {/* Quick presets */}
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  {[3, 5, 7, 14, 30].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setDays(preset)}
                      className={`px-3 py-1 rounded-md text-xs font-medium border transition ${
                        Number(days) === preset
                          ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                          : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </section>

              {/* Info note */}
              <div className="mb-6 p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-200/90 leading-relaxed">
                  {enabled ? (
                    <>
                      <strong>{t('الحالة الحاليّة: مفعّل ✅', 'Current status: ON ✅')}</strong>
                      <br />
                      {t(
                        `عندما ترفض طلب وكيل، سيُخزَّن تاريخ الرفض في منصّة التجار. الوكيل لن يتمكّن من التقديم مجدداً حتى مرور ${days} يوم/أيام.`,
                        `When you reject an application, the merchant platform stores the rejection date. The applicant cannot reapply until ${days} day(s) pass.`,
                      )}
                    </>
                  ) : (
                    <>
                      <strong>{t('الحالة الحاليّة: مُوقَف ⏸', 'Current status: OFF ⏸')}</strong>
                      <br />
                      {t(
                        'أي وكيل تُرفض طلبه يمكنه التقديم مباشرة مرة أخرى — دون انتظار.',
                        'A rejected applicant can reapply immediately — no waiting period enforced.',
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Save + meta */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-white/40">
                  {meta?.updated_at && (
                    <>
                      <Shield className="inline h-3 w-3 me-1" />
                      {t('آخر تحديث:', 'Last updated:')}{' '}
                      {new Date(meta.updated_at).toLocaleString(isAr ? 'ar-SA' : 'en-US')}
                      {meta.updated_by && ` · ${meta.updated_by}`}
                    </>
                  )}
                </div>
                <button
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#d4af37] text-black font-bold hover:bg-[#d4af37]/90 disabled:opacity-40 transition"
                  data-testid="cooldown-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {t('حفظ الإعدادات', 'Save Settings')}
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
